import React from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import {Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {menuContext} from 'components/ContextMenu/context';
import {useBuildHeaderMenuContext} from 'components/ContextMenu/hooks/useBuildHeaderMenuContext';
import {useMenu} from 'components/Menu/hooks';

type MenuProps = {
   name: React.ReactNode;
   children: React.ReactNode;
   hideExpandIcon?: boolean;
   disabled?: boolean;
   id?: string;
};

const useStyles = makeStyles((theme: Theme) => ({
   root: {
      display: 'flex',
   },
   paper: {
      maxHeight: '85vh',
      overflowY: 'auto',
      overflowX: 'visible',
      marginTop: theme.spacing(1),
      '& .MuiList-padding': {
         padding: 0,
      },
   },
   anchor: {
      textTransform: 'none',
   },
}));

const Menu = ({name, children, hideExpandIcon, disabled, id}: MenuProps) => {
   const classes = useStyles();
   const {
      open,
      anchorRef,
      handleToggle,
      handleClose,
      handleListKeyDown,
   } = useMenu<HTMLButtonElement>();
   const {Provider} = menuContext;
   const {contextValue} = useBuildHeaderMenuContext({handleClose});

   return (
      <div className={classes.root}>
         <div>
            <Button
               id={id}
               className={classes.anchor}
               ref={anchorRef}
               aria-controls={open ? 'menu-list-grow' : undefined}
               aria-haspopup="true"
               onClick={handleToggle}
               disabled={disabled}
            >
               {name} {!hideExpandIcon && <ExpandMoreRoundedIcon />}
            </Button>
            <Popper
               open={open}
               anchorEl={anchorRef.current}
               placement="bottom-start"
               role={undefined}
               transition
               disablePortal
               sx={{
                  zIndex: '99',
               }}
            >
               {({TransitionProps}) => (
                  <Grow
                     {...TransitionProps}
                     style={{
                        transformOrigin: 'top',
                     }}
                  >
                     <Paper className={classes.paper} elevation={5}>
                        <ClickAwayListener onClickAway={handleClose}>
                           <MenuList
                              autoFocusItem={open}
                              variant="menu"
                              id="menu-list-grow"
                              onKeyDown={handleListKeyDown}
                           >
                              <Provider value={contextValue}>{children}</Provider>
                           </MenuList>
                        </ClickAwayListener>
                     </Paper>
                  </Grow>
               )}
            </Popper>
         </div>
      </div>
   );
};

export default Menu;
